<template>
<!--
  Route name: vecdyn-subassay-upload
  Local URL: http://localhost:8080/vecdyn-subassay-upload/:id
  Required Group Membership: Uploader, Curator or Admin
  Purpose: Upload linked-assays (subassay) CSV for a VecDyn dataset. Backend (StageLinkedAssaysUpload) validates
  each row against the dataset's TimeSeriesData by linked_assay_id. Response: { rows: [ { row_number, data, accepted, rejection_reason }, ... ] }.
  No file is saved; display is validation-only. User can fix errors and re-upload.
-->
  <VDBaseTemplate>
    <b-modal
      id="file_working-modal"
      v-model="showFileModal"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Getting File"
      :header-text-variant="'light'">
      Working on file. Please wait...
      <b-button class="mt-3" block @click="showFileModal = !showFileModal">OK</b-button>
    </b-modal>
    <b-modal
      id="api-err-modal"
      v-model="apiErrModalShow"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Error"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'">
      <span v-html="err_msg"></span>
      <b-button class="mt-3" block @click="hideModal">OK</b-button>
    </b-modal>

    <!-- This modal shows while the file is updated and processed. -->
    <b-modal
      id="warning-modal"
      v-model="workingModalShow"
      centered
      scrollable
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      title="Processing File"
      :header-bg-variant="'warning'"
      :header-text-variant="'light'">
      <span v-html="'Please wait while the file is processed. This may take several minutes.'"></span>
    </b-modal>
    <div class="w-100 h-100 pb-4">
      <b-container fluid class="pt-5 mt-5">
        <h2 class="tw-mb-0 text-aegis-dk">Submit VecDyn SubAssay Data for Dataset ID: {{ id }}</h2>
        <!-- Loading state while checking if dataset already has subassays -->
        <b-container fluid v-if="loadingHasSubassays" class="py-5">
          <p class="tw-text-gray-700">Checking whether this dataset has existing SubAssays...</p>
        </b-container>

        <!-- Dataset already has SubAssays: must delete before new upload. Offer download + delete. -->
        <b-container fluid v-else-if="hasSubassays" class="py-4">
          <div class="tw-rounded tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-4">
            <p class="tw-font-medium tw-text-gray-800 tw-mb-2">
              This dataset already has SubAssays assigned to rows.
            </p>
            <p class="tw-text-gray-700 tw-mb-4">
              You must delete the existing SubAssays before uploading a new file. To edit SubAssays:
              download the current data as CSV, edit it, delete all SubAssays for this dataset here, then upload the edited file.
            </p>
            <div class="d-flex flex-wrap align-items-center">
              <b-button variant="primary" @click="downloadCurrentSubassays" :disabled="downloadSubassaysInProgress" class="mr-2">
                Download current SubAssays (CSV)
              </b-button>
              <b-button variant="danger" @click="confirmDeleteSubassays" :disabled="deleteSubassaysInProgress" class="mr-2">
                Delete all SubAssays for this dataset
              </b-button>
              <router-link
                :to="{ name: 'vecdyn-detail', params: { id: id } }"
                class="btn btn-primary js-tooltip ml-1 ml-md-auto button-link"
              >
                Return to Dataset
              </router-link>
            </div>
          </div>
        </b-container>

        <!-- No existing subassays: show instructions and upload flow -->
        <template v-else>
          <!-- Instructions for linked-assays (subassay) upload. Backend validates each row by linked_assay_id. -->
          <b-button v-b-toggle.collapse-instructions variant="primary">Instructions</b-button>
          <b-collapse id="collapse-instructions">
            <div style="padding:15px;">
              <p><b>Submitting Linked-Assay (Subassay) Data</b></p>
              <ol type="1">
                <li>
                  Download the template in CSV format by clicking the "Download" button (optional).
                </li>
                <li>
                  Your CSV must include a <b>linked_assay_id</b> column. Each value must match exactly one
                  <code>linked_assay_id</code> in this dataset's Time Series Data. Headers may be camelCase or snake_case.
                </li>
                <li>
                  Upload your file using "Browse" below, then click "Upload". The system will validate each row
                  and show which rows are accepted and which are rejected (with a reason).
                </li>
                <li>
                  Use "Commit SubAssay to Database" after validation to save. To edit later: download current SubAssays, delete them, then re-upload.
                </li>
              </ol>
              <b>Required:</b> Each row must have a <b>linked_assay_id</b> that exists exactly once in this dataset.
            </div>
          </b-collapse>
          <b-button variant="primary" class="ml-1" @click="downloadTemplate">Download Template</b-button>
          <router-link
                  :to="{ name: 'vecdyn-detail', params: { id: id } }"
                  class="btn btn-primary js-tooltip ml-1 button-link"
                  style="float: right;"
                >
                  Return to Dataset
                </router-link>
        </template>
      </b-container>
        <hr />
        <!-- UPLOAD FORM: only when no existing subassays and no results yet -->
        <b-container fluid v-if="!hasSubassays && !loadingHasSubassays" v-show="!show_summary" style="padding: 50px;">
          <b-row>
            <b-col md="6" offset-md="3" class="mt-3">
              <b-form-file
                id="file"
                v-model="selectedFile"
                :state="Boolean(selectedFile)"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
                size="lg"
              ></b-form-file>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6" offset-md="3" class="mt-3">
              <div style="float: right; display: inline;">
                <b-button @click="onUpload()" variant="primary" class="mr-1">Upload</b-button>
                <!--<router-link
                  :to="{ name: 'vecdyn-detail', params: { id: id } }"
                  class="btn btn-primary js-tooltip ml-1 button-link"
                  style="float: right;"
                >
                  Return to Dataset
                </router-link> -->
              </div>
            </b-col>
          </b-row>
        </b-container>
        <!-- END UPLOAD FORM SECTION -->

      <!-- SUMMARY SECTION: total / accepted / rejected from StageLinkedAssaysUpload response.
           Rejected rows are shown in the table with visual styling (_rowVariant); no separate error block. -->
      <b-container fluid v-if="!hasSubassays && !loadingHasSubassays && show_summary" style="padding-right: 50px; padding-left: 50px; margin-bottom: 2rem;">
        <h3>Validation summary</h3>
        <b-row><b-col><b>Total rows:</b> {{ summary.total_rows }}</b-col></b-row>
        <b-row><b-col><b>Accepted:</b> {{ summary.accepted_count }}</b-col></b-row>
        <b-row v-if="summary.rejected_count > 0" style="color: red;"><b-col><b>Rejected:</b> {{ summary.rejected_count }}</b-col></b-row>
        <b-row>
          <VBUploadObjectsContinuousTable :tableData="table_data" :fields="row_table_fields" :objectName="'Row result'" :type="'summary'"/>
        </b-row>
        <b-row style="margin-bottom: 1rem;">
          <b-col>
            <div style="float: right; display: inline;" class="mb-4">
              <b-button @click="clearPageState()">Clear results</b-button>
              <b-button
                @click="onCommit()"
                variant="success"
                class="ml-1"
                :disabled="commitInProgress || !selectedFile || summary.rejected_count > 0"
              >
                Commit SubAssay to Database
              </b-button>
              <router-link
                :to="{ name: 'vecdyn-detail', params: { id: id } }"
                class="btn btn-primary js-tooltip ml-1 button-link"
                style="float: right;"
              >
                Return to Dataset
              </router-link>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </VDBaseTemplate>
</template>

<script>
// Submit VecDyn subassay data for a specific dataset (id from route params).
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";
import VDBaseTemplate from "@/layouts/VDBaseTemplate.vue";
import VBUploadObjectsContinuousTable from "@/components/tables/VBUploadObjectsContinuousTable.vue";
import _ from "lodash";

export default {
  name: "submit-vecdyn-subassay",
  mixins: [VDBaseTemplate],
  components: {
    VDBaseTemplate,
    VBUploadObjectsContinuousTable
  },
  // Dataset id from route (vecdyn-detail/:id); used to associate subassay upload with parent dataset.
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data(){
    return{
      // Whether this dataset already has SubAssays (null = not yet loaded).
      hasSubassays: null,
      loadingHasSubassays: true,
      downloadSubassaysInProgress: false,
      deleteSubassaysInProgress: false,
      table_data: [],
      // Summary derived from StageLinkedAssaysUpload response: { rows: [...] }
      summary: {
        total_rows: 0,
        accepted_count: 0,
        rejected_count: 0,
      },
      show_summary: false,
      selectedFile: null,
      err_msg: '',
      apiErrModalShow: false,
      workingModalShow: false,
      showFileModal: false,
      commitInProgress: false,
      // Built from first row in table_data so all data keys get a column (row_number, accepted, rejection_reason, ...data).
      row_table_fields: [],
    };
  },
  computed: {},
  watch: {},
  created() {
    document.title = "Submit VecDyn Subassay Data";
    this.fetchHasSubassays();
  },
  methods: {
    hideModal() {
      this.apiErrModalShow = false;
    },
    /** GET has-subassays; sets hasSubassays and loadingHasSubassays. */
    fetchHasSubassays() {
      this.loadingHasSubassays = true;
      HTTP.get(`/api/vecdyn-dataset/${this.id}/has-subassays/`)
        .then((response) => {
          this.hasSubassays = response.data.has_subassays === true;
          this.loadingHasSubassays = false;
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          this.loadingHasSubassays = false;
        });
    },
    /** Download current SubAssays for this dataset as CSV (same format as stage/commit). */
    downloadCurrentSubassays() {
      this.downloadSubassaysInProgress = true;
      HTTP.get(`/api/vecdyn-dataset/${this.id}/subassays/download/`, { responseType: "arraybuffer" })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]));
          const fileLink = document.createElement("a");
          fileLink.href = fileURL;
          fileLink.setAttribute("download", `subassays_dataset_${this.id}.csv`);
          document.body.appendChild(fileLink);
          fileLink.click();
          document.body.removeChild(fileLink);
          window.URL.revokeObjectURL(fileURL);
          this.$notify({ text: "Download started.", duration: 3000, type: "success" });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
        })
        .finally(() => {
          this.downloadSubassaysInProgress = false;
        });
    },
    /** Confirm then DELETE all SubAssays for this dataset; re-fetch has_subassays on success. */
    confirmDeleteSubassays() {
      this.$bvModal
        .msgBoxConfirm(
          "Delete all SubAssays for this dataset? This cannot be undone. You can re-upload after deleting.",
          {
            title: "Delete SubAssays",
            okVariant: "danger",
            okTitle: "Delete all",
            cancelTitle: "Cancel",
            centered: true,
          }
        )
        .then((confirmed) => {
          if (!confirmed) return;
          this.deleteSubassaysInProgress = true;
          HTTP.delete(`/api/vecdyn-dataset/${this.id}/subassays/`)
            .then((response) => {
              const n = response.data.deleted_count != null ? response.data.deleted_count : 0;
              this.$notify({
                text: `${n} SubAssay record(s) deleted. You can now upload a new file.`,
                duration: 8000,
                type: "success",
              });
              this.hasSubassays = false;
            })
            .catch((error) => {
              this.err_msg = HandleErrors.HandleErrors(error);
              this.apiErrModalShow = true;
            })
            .finally(() => {
              this.deleteSubassaysInProgress = false;
            });
        });
    },
    onFileSelected: function (event) {
      this.selectedFile = event.target.files[0];
    },
    clearPageState: function () {
      this.table_data = [];
      this.summary.total_rows = 0;
      this.summary.accepted_count = 0;
      this.summary.rejected_count = 0;
      this.show_summary = false;
      this.selectedFile = null;
      this.row_table_fields = [];
      this.err_msg = '';
      this.commitInProgress = false;
    },
    /**
     * POST same file + dataset_id to CommitLinkedAssaysUpload. Backend validates again and creates SubAssay
     * for each accepted row; returns { rows, created_count }. We refresh the table from rows and show created_count.
     */
    onCommit() {
      if (!this.selectedFile) {
        this.$bvModal.msgBoxOk("The original file is no longer available. Please upload again and then commit.", {
          title: "Cannot commit",
          okVariant: "warning",
          centered: true,
        });
        return;
      }
      this.commitInProgress = true;
      const fd = new FormData();
      fd.append("file", this.selectedFile, this.selectedFile.name);
      fd.append("dataset_id", this.id);

      HTTP.post("/api/vecdyn-subassay-commit/", fd)
        .then((response) => {
          const rows = response.data.rows;
          const createdCount = response.data.created_count != null ? response.data.created_count : 0;
          if (rows != null) {
            this.buildDisplayFromRows(rows);
          }
          this.commitInProgress = false;
          this.$notify({
            text: `Committed: ${createdCount} SubAssay record(s) created.`,
            duration: 10000,
            type: "success",
          });
          // Dataset now has subassays; show download/delete panel (no extra network call).
          this.hasSubassays = true;

          this.$router.push({
            name: "vecdyn-detail",
            params: { id: this.id }
          });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          this.commitInProgress = false;
        });
        
    },
    /**
     * Build table_data with _rowVariant for rejected rows so the main table shows them with danger styling.
     */
    buildDisplayFromRows(rows) {
      if (!Array.isArray(rows) || rows.length === 0) {
        this.table_data = [];
        this.summary.total_rows = 0;
        this.summary.accepted_count = 0;
        this.summary.rejected_count = 0;
        this.row_table_fields = [];
        return;
      }
      const tableRows = rows.map((r) => {
        const flat = {
          row_number: r.row_number,
          accepted: r.accepted ? 'Yes' : 'No',
          rejection_reason: r.rejection_reason || '',
          ...(r.data || {}),
        };
        // Bootstrap-Vue b-table uses _rowVariant for row background (e.g. danger = red).
        if (!r.accepted) {
          flat._rowVariant = 'danger';
        }
        return flat;
      });
      this.table_data = tableRows;
      this.summary.total_rows = rows.length;
      this.summary.accepted_count = rows.filter((r) => r.accepted).length;
      this.summary.rejected_count = rows.filter((r) => !r.accepted).length;
      // Build row_table_fields from first item keys (skip _rowVariant so it doesn't show as a column).
      const firstKeys = tableRows.length > 0 ? Object.keys(tableRows[0]).filter((k) => k !== '_rowVariant') : [];
      this.row_table_fields = firstKeys.map((key) => ({
        key,
        label: key.replace(/_/g, ' '),
        sortable: true,
        tdClass: 'text-truncate vt-table-cell',
      }));
    },
    onUpload: function () {
      const that = this;
      if (!this.selectedFile) {
        return that.$bvModal.msgBoxOk("Please select a file!", {
          title: "File Error",
          okVariant: "danger",
          centered: true,
        });
      }
      this.workingModalShow = true;
      const fd = new FormData();
      fd.append("file", this.selectedFile, this.selectedFile.name);
      fd.append("dataset_id", this.id);

      HTTP.post("/api/vecdyn-subassay-stage/", fd)
        .then((response) => {
          const rows = response.data.rows;
          if (rows == null) {
            this.err_msg = "Unexpected response: missing 'rows'.";
            this.apiErrModalShow = true;
            this.workingModalShow = false;
            return;
          }
          this.buildDisplayFromRows(rows);
          this.show_summary = true;
          this.workingModalShow = false;
          this.$notify({
            text: "File validated.",
            duration: 5000,
            type: "success",
          });
        })
        .catch((error) => {
          this.err_msg = HandleErrors.HandleErrors(error);
          this.apiErrModalShow = true;
          this.workingModalShow = false;
        });
    },
    downloadTemplate(){
      var params = new URLSearchParams();
      // Pass dataset id so backend can return dataset-specific template if needed.
      params.append("dataset_id", this.id);
      var request = {
        params: params,
        responseType: "arraybuffer",
      };

      HTTP.get(`/api/vecdyn-subassay-template-download/`, request)
      .then((response) => {
        var fileURL = window.URL.createObjectURL(
          new Blob([response.data])
        );
        var fileLink = document.createElement("a");
        fileLink.href = fileURL;
        var fileName = "vecdyn_subassay_template.csv";
        fileLink.setAttribute("download", fileName);
        document.body.appendChild(fileLink);
        fileLink.click();
      })
      .catch((error) => {
        this.err_msg = HandleErrors.HandleErrors(error);
        this.apiErrModalShow = true;
      });
    },
    downloadCSV: function (items, title) {
      this.showFileModal = true;
      var columns = _.keys(items[0]);
      var csv = columns.join(",") + "\n";
      items.forEach(function (row) {
        csv += _.values(row).join(",");
        csv += "\n";
      });
      this.showFileModal = false;
      var hiddenElement = document.createElement("a");
      hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
      hiddenElement.target = "_blank";
      hiddenElement.download = title + ".csv";
      hiddenElement.click();
    }
  },
  beforeRouteLeave(to, from, next) {
    // No staged file on backend; leaving just discards local results.
    next();
  },
};
</script>

<style scoped>
.table-head {
  background: #777;
  font-weight: bold;
  text-align: left;
  padding: 5px;
}

.btn-success {
  background: var(--vb_green);
}
</style>
